/**
 * Settings.ts - 사용자 설정 (조작키 / 언어 / 기타 옵션) 저장소
 * - localStorage('terra_anima_settings')에 저장
 * - 조작키는 시스템/전투 예약 키를 지정할 수 없다 (F12는 시스템 키 전용 안내)
 */

export type BindAction = 'character' | 'inventory' | 'party' | 'guild' | 'craft' | 'map';
export type Language = 'ko' | 'en' | 'ja';

export const BIND_ACTIONS: Array<{ id: BindAction; label: string }> = [
  { id: 'character', label: '캐릭터 스탯창' },
  { id: 'inventory', label: '100칸 인벤토리' },
  { id: 'party', label: '파티창' },
  { id: 'guild', label: '길드 정보' },
  { id: 'craft', label: '제작 & 인챈터' },
  { id: 'map', label: '전체 월드맵' },
];

const DEFAULT_BINDINGS: Record<BindAction, string> = {
  character: 'c',
  inventory: 'i',
  party: 'o',
  guild: 'g',
  craft: 'k',
  map: 'm',
};

/** 이동/타겟팅/스킬 등 이미 게임이 사용 중인 키 */
const RESERVED_KEYS = new Set([
  'escape', 'tab', 'enter', ' ', 'space',
  'w', 'a', 's', 'd', 'f', 't', 'h',
  '1', '2', '3', '4', '5', '6', '7', '8',
]);

interface StoredSettings {
  bindings: Record<BindAction, string>;
  language: Language;
  values: Record<string, string | boolean>;
}

const STORAGE_KEY = 'terra_anima_settings';

function load(): StoredSettings {
  const base: StoredSettings = { bindings: { ...DEFAULT_BINDINGS }, language: 'ko', values: {} };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        bindings: { ...DEFAULT_BINDINGS, ...(p.bindings || {}) },
        language: p.language === 'en' || p.language === 'ja' ? p.language : 'ko',
        values: p.values || {},
      };
    }
  } catch {
    /* ignore corrupted settings */
  }
  return base;
}

let cache: StoredSettings = load();

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore */
  }
}

export function normalizeKey(key: string): string {
  return key.length === 1 ? key.toLowerCase() : key.toLowerCase();
}

export function displayKey(key: string): string {
  return key.length === 1 ? key.toUpperCase() : key.replace(/^./, (c) => c.toUpperCase());
}

export const Settings = {
  getBinding(action: BindAction): string {
    return cache.bindings[action];
  },

  getBindings(): Record<BindAction, string> {
    return { ...cache.bindings };
  },

  /** 눌린 키에 해당하는 액션 (없으면 null) */
  actionForKey(key: string): BindAction | null {
    const k = normalizeKey(key);
    for (const a of BIND_ACTIONS) {
      if (cache.bindings[a.id] === k) return a.id;
    }
    return null;
  },

  /** 키 지정 시도. 실패하면 사유 메시지를 돌려준다. */
  setBinding(action: BindAction, rawKey: string): { ok: boolean; message?: string } {
    const key = normalizeKey(rawKey);
    if (key === 'f12') {
      return { ok: false, message: 'F12는 시스템 키이므로 지정할 수 없습니다.' };
    }
    if (/^f([1-9]|1[0-1])$/.test(key)) {
      return { ok: false, message: `${displayKey(key)}는 시스템/파티 선택 예약 키이므로 지정할 수 없습니다.` };
    }
    if (['shift', 'control', 'alt', 'meta', 'capslock', 'dead', 'process'].includes(key)) {
      return { ok: false, message: '해당 키는 단축키로 지정할 수 없습니다.' };
    }
    if (RESERVED_KEYS.has(key)) {
      return { ok: false, message: `${displayKey(key)} 키는 이동/전투에서 사용 중인 예약 키입니다.` };
    }
    const dup = BIND_ACTIONS.find((a) => a.id !== action && cache.bindings[a.id] === key);
    if (dup) {
      return { ok: false, message: `${displayKey(key)} 키는 이미 [${dup.label}]에 지정되어 있습니다.` };
    }
    cache.bindings[action] = key;
    persist();
    return { ok: true };
  },

  resetBindings(): void {
    cache.bindings = { ...DEFAULT_BINDINGS };
    persist();
  },

  getLanguage(): Language {
    return cache.language;
  },

  setLanguage(lang: Language): void {
    cache.language = lang;
    persist();
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  },

  getValues(): Record<string, string | boolean> {
    return { ...cache.values };
  },

  setValues(values: Record<string, string | boolean>): void {
    cache.values = { ...values };
    persist();
  },
};

if (typeof document !== 'undefined') document.documentElement.lang = cache.language;
